// Single source of truth for the Rails CSRF token. Import from here rather
// than reading the meta tag directly.
//
// <meta name="csrf-token"> is written once, when the document loads. Any
// server-side reset_session — instructor and reviewer magic-link login, and
// start_new_session_for on every normal login — rotates session[:_csrf_token]
// and strands that tag. Long-lived SPA pages (the broadcast console runs for
// hours, and a viewer holds the webinar room open just as long)
// then fail every request, because Rails verifies the token in a filter that
// runs ahead of authentication, so the request 422s before any application
// code sees it.
//
// InertiaRails::Controller installs an after_action that rewrites the
// XSRF-TOKEN cookie with a currently-valid token on every response, and
// ApplicationController includes that concern. The cookie therefore tracks the
// live session for the life of the page where the meta tag cannot.
//
// Sent as X-CSRF-Token, which Rails reads natively. X-XSRF-TOKEN also works —
// InertiaRails' middleware copies it across — but that adds a dependency on
// the middleware staying in the stack for a non-Inertia fetch.

export function getCsrfToken() {
  // Decoded because Rack percent-encodes cookie values. Currently a no-op:
  // load_defaults 8.0 sets urlsafe_csrf_tokens, so the token is base64url and
  // has nothing to escape. It stops being a no-op if that default is turned
  // off, at which point +, / and = arrive as %2B, %2F and %3D.
  const cookie = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)?.[1];
  if (cookie) return decodeURIComponent(cookie);

  // No cookie means forgery protection is off for this response — the test
  // environment disables it globally — where the meta tag is still rendered.
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

export function csrfFetch(url, options = {}) {
  const headers = new Headers(options.headers || {});
  headers.set('X-CSRF-Token', getCsrfToken());
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  return fetch(url, { credentials: 'same-origin', ...options, headers });
}

export function postJSON(url, body, options = {}) {
  const headers = new Headers(options.headers || {});
  headers.set('Content-Type', 'application/json');
  return csrfFetch(url, {
    ...options,
    method: 'POST',
    headers,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}
